import { useState, useRef, useEffect, useMemo, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { FunnelIcon } from '@heroicons/react/24/outline'
import clsx from 'clsx'

export type FilterOperator = 'contains' | 'equals'

export interface ColumnFilterPopoverProps {
  /** Stable list of every option the column can take, in display order. */
  options: string[]
  /**
   * Current selection. `null` means "all values selected" (no active filter).
   * An empty array means "nothing matches" (active filter that hides every row).
   */
  value: string[] | null
  onChange: (value: string[] | null) => void
  /** Optional display labels keyed by raw option value. */
  labels?: Record<string, string>
  /** Applies a free-text contains/equals filter in the parent table when set. */
  onTextFilterChange?: (text: string) => void
  textFilter?: string
  /** Optional aria/test handle for the trigger button. */
  testId?: string
}

/**
 * Excel-style column filter popover. Pairs a value checklist with an
 * optional `contains` / `equals` text filter. Renders into `document.body`
 * via a portal so it can escape clipping containers (e.g. a table with
 * `overflow: hidden`). Used in `PassAnalyticsTable` to add per-column
 * filtering on top of the existing sort + Min/Avg/Max header.
 */
export function ColumnFilterPopover({
  options,
  value,
  onChange,
  labels,
  onTextFilterChange,
  textFilter = '',
  testId,
}: ColumnFilterPopoverProps) {
  const [open, setOpen] = useState(false)
  const [operator, setOperator] = useState<FilterOperator>('contains')
  const [filterQuery, setFilterQuery] = useState(textFilter)
  const [searchQuery, setSearchQuery] = useState('')
  const buttonRef = useRef<HTMLButtonElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState({ top: 0, left: 0 })

  useEffect(() => {
    if (!open) setFilterQuery(textFilter)
  }, [open, textFilter])

  const updatePos = useCallback(() => {
    if (!buttonRef.current) return
    const rect = buttonRef.current.getBoundingClientRect()
    const dropdownWidth = 280
    const left =
      rect.left + dropdownWidth > window.innerWidth
        ? rect.right - dropdownWidth
        : rect.left
    setPos({ top: rect.bottom + 4, left: Math.max(4, left) })
  }, [])

  useEffect(() => {
    if (open) updatePos()
  }, [open, updatePos])

  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      const target = e.target as Node
      if (buttonRef.current?.contains(target)) return
      if (dropdownRef.current?.contains(target)) return
      setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  useEffect(() => {
    if (!open) return
    const handler = (e: Event) => {
      if (dropdownRef.current?.contains(e.target as Node)) return
      setOpen(false)
    }
    window.addEventListener('scroll', handler, true)
    return () => window.removeEventListener('scroll', handler, true)
  }, [open])

  useEffect(() => {
    if (!open) setSearchQuery('')
  }, [open])

  const isActive = value !== null || Boolean(textFilter.trim())
  const selectedSet = useMemo(() => new Set(value ?? []), [value])
  const allValues = options

  const visibleOptions = useMemo(() => {
    let list = options
    const fq = filterQuery.trim().toLowerCase()
    if (fq) {
      list = list.filter((opt) => {
        const label = (labels?.[opt] ?? opt).toLowerCase()
        return operator === 'contains' ? label.includes(fq) : label === fq
      })
    }
    const sq = searchQuery.trim().toLowerCase()
    if (sq) {
      list = list.filter((opt) => (labels?.[opt] ?? opt).toLowerCase().includes(sq))
    }
    return list
  }, [options, filterQuery, searchQuery, labels, operator])

  const allChecked = value === null
  const someChecked =
    value !== null && selectedSet.size > 0 && selectedSet.size < allValues.length

  const handleSelectAll = () => {
    onChange(allChecked ? [] : null)
  }

  const handleToggle = (val: string) => {
    if (value === null) {
      onChange(allValues.filter((v) => v !== val))
    } else if (selectedSet.has(val)) {
      const next = value.filter((v) => v !== val)
      onChange(next.length === 0 ? [] : next)
    } else {
      const next = [...value, val]
      onChange(next.length === allValues.length ? null : next)
    }
  }

  const handleFilterInput = (text: string) => {
    setFilterQuery(text)
    onTextFilterChange?.(text)
  }

  const handleReset = () => {
    setFilterQuery('')
    setSearchQuery('')
    setOperator('contains')
    onTextFilterChange?.('')
    onChange(null)
  }

  const dropdown = open
    ? createPortal(
        <div
          ref={dropdownRef}
          data-testid={testId ? `${testId}-popover` : undefined}
          style={{ position: 'fixed', top: pos.top, left: pos.left }}
          className="z-[9999] bg-white rounded-lg border border-[#E2E5EA] shadow-[0_4px_16px_rgba(0,0,0,0.12)] min-w-[260px] max-w-[320px]"
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div className="p-2 border-b border-gray-100 space-y-2">
            <select
              aria-label="Filter operator"
              value={operator}
              onChange={(e) => setOperator(e.target.value as FilterOperator)}
              className=" w-full rounded-md border border-[#D8DDE5] bg-white px-2.5 font-sans text-[12px] text-blueprint-blue outline-none transition-colors placeholder:text-heritage-grey focus:border-hero-red"
            >
              <option value="contains">Contains</option>
              <option value="equals">Equals</option>
            </select>
            <input
              type="text"
              value={filterQuery}
              onChange={(e) => handleFilterInput(e.target.value)}
              placeholder="Filter..."
              className="h-8 w-full rounded-md border border-[#D8DDE5] bg-white px-2.5 font-sans text-[12px] text-blueprint-blue outline-none transition-colors placeholder:text-heritage-grey focus:border-hero-red"
            />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search..."
              className="h-8 w-full rounded-md border border-[#D8DDE5] bg-white px-2.5 font-sans text-[12px] text-blueprint-blue outline-none transition-colors placeholder:text-heritage-grey focus:border-hero-red"
            />
          </div>

          <div className="max-h-48 overflow-y-auto py-1">
            <label className="flex items-center gap-2 px-3 py-1.5 font-sans text-[12px] text-blueprint-blue cursor-pointer hover:bg-page-bg/80 border-b border-gray-100 font-medium">
              <input
                type="checkbox"
                checked={allChecked}
                ref={(el) => {
                  if (el) el.indeterminate = someChecked
                }}
                onChange={handleSelectAll}
                className="h-3.5 w-3.5 shrink-0 cursor-pointer rounded border-heritage-grey text-hero-red focus:outline-none focus:ring-2 focus:ring-hero-red/40"
              />
              <span>(Select All)</span>
            </label>

            {visibleOptions.length === 0 && (
              <div className="px-3 py-2 text-[11px] font-sans text-heritage-grey">
                No matching values.
              </div>
            )}

            {visibleOptions.map((opt) => {
              const checked = value === null || selectedSet.has(opt)
              const label = labels?.[opt] ?? opt
              return (
                <label
                  key={opt}
                  htmlFor={`col-filter-${testId ?? 'col'}-${opt}`}
                  className="flex cursor-pointer items-center gap-2 px-3 py-1.5 font-sans text-[12px] text-text-grey hover:bg-page-bg/80"
                >
                  <input
                    id={`col-filter-${testId ?? 'col'}-${opt}`}
                    type="checkbox"
                    checked={checked}
                    onChange={() => handleToggle(opt)}
                    className="h-3.5 w-3.5 shrink-0 cursor-pointer rounded border-heritage-grey text-hero-red focus:outline-none focus:ring-2 focus:ring-hero-red/40"
                  />
                  <span className="truncate flex-1">{label}</span>
                </label>
              )
            })}
          </div>

          <div className="px-2.5 py-2 border-t border-[#EEF0F3] flex justify-end">
            <button
              type="button"
              onClick={handleReset}
              data-testid={testId ? `${testId}-reset` : undefined}
              className="rounded px-1 font-sans text-[12px] font-semibold text-readable-red transition-colors hover:text-hero-red focus:outline-none focus-visible:ring-2 focus-visible:ring-hero-red focus-visible:ring-offset-2"
            >
              Reset
            </button>
          </div>
        </div>,
        document.body
      )
    : null

  return (
    <span className="relative inline-flex items-center">
      <button
        type="button"
        ref={buttonRef}
        data-testid={testId}
        onClick={(e) => {
          e.stopPropagation()
          setOpen(!open)
        }}
        onMouseDown={(e) => e.stopPropagation()}
        className={clsx(
          'p-0.5 ml-0.5 transition-colors relative flex items-center',
          isActive ? 'text-readable-red' : 'text-heritage-grey hover:text-blueprint-blue'
        )}
        title="Filter column"
        aria-label="Filter column"
      >
        <FunnelIcon className="w-3.5 h-3.5 stroke-[2]" />
        {value !== null && value.length > 0 && (
          <span className="absolute -top-1.5 -right-1.5 bg-readable-red text-white text-[9px] font-bold rounded-full w-3.5 h-3.5 flex items-center justify-center leading-none">
            {selectedSet.size}
          </span>
        )}
      </button>
      {dropdown}
    </span>
  )
}

export default ColumnFilterPopover
